import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import { FaSave } from "react-icons/fa";
import { Icon } from "../../../../../components/common/Icon";

interface EditSourceModalProps {
    show: boolean;
    onHide: () => void;
    chatbotId?: number;
    source: any;
    onSubmit: (data: {
        title: string;
        lang: string;
        text?: string;
        file?: File;
    }) => Promise<void>;
    isSubmitting: boolean;
}

const EditSourceModal: React.FC<EditSourceModalProps> = ({
    show,
    onHide,
    chatbotId,
    source,
    onSubmit,
    isSubmitting,
}) => {
    const [title, setTitle] = useState("");
    const [lang, setLang] = useState("tr");
    const [text, setText] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loadingContent, setLoadingContent] = useState(false);
    const [uploadPath, setUploadPath] = useState<string | null>(null);

    const isFile = source?.type?.toLowerCase() === "file";

    // Load source data and content when modal opens
    useEffect(() => {
        if (show && source) {
            setTitle(source.title || "");
            setLang(source.lang || "tr");
            setText("");
            setFile(null);
            setError(null);
            setUploadPath(null);

            // Get content from latest_job.stats
            const stats = source.latest_job?.stats || {};

            if (!isFile) {
                // For text sources, load pending_text from stats
                setText(stats.pending_text || "");
            } else {
                // For file sources, get upload_path from stats
                setUploadPath(stats.upload_path || null);
            }
        }
    }, [show, source, isFile]);

    const handleSubmit = async () => {
        setError(null);

        if (!title.trim()) {
            setError("Please enter a title for the source.");
            return;
        }

        if (!isFile && !text.trim()) {
            setError("Please enter the text content.");
            return;
        }

        if (isFile && !file) {
            setError("Please select a file to upload.");
            return;
        }

        try {
            await onSubmit({
                title,
                lang,
                text: !isFile ? text : undefined,
                file: isFile ? file! : undefined,
            });
            onHide();
        } catch (err: any) {
            setError(err.message || "Failed to update source");
        }
    };

    const languages = [
        { code: "en", name: "English", flag: "🇬🇧" },
        { code: "tr", name: "Türkçe", flag: "🇹🇷" },
        { code: "de", name: "Deutsch", flag: "🇩🇪" },
        { code: "fr", name: "Français", flag: "🇫🇷" },
        { code: "es", name: "Español", flag: "🇪🇸" },
        { code: "it", name: "Italiano", flag: "🇮🇹" },
        { code: "pt", name: "Português", flag: "🇵🇹" },
        { code: "ru", name: "Русский", flag: "🇷🇺" },
        { code: "ar", name: "العربية", flag: "🇸🇦" },
        { code: "ja", name: "日本語", flag: "🇯🇵" },
        { code: "ko", name: "한국어", flag: "🇰🇷" },
        { code: "zh", name: "中文", flag: "🇨🇳" },
    ];

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    Edit Source
                    {source && <small className="text-muted ms-2">#{source.id}</small>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                <Form>
                    <div className="row g-3">
                        <div className="col-md-8">
                            <Form.Group>
                                <Form.Label>Source Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g. Company Policy, Product Manual"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    autoFocus
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Label>Language</Form.Label>
                                <Form.Select
                                    value={lang}
                                    onChange={(e) => setLang(e.target.value)}
                                >
                                    {languages.map((l) => (
                                        <option key={l.code} value={l.code}>
                                            {l.flag} {l.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </div>



                        <div className="col-12">
                            {!isFile ? (
                                <Form.Group>
                                    <Form.Label>Content</Form.Label>
                                    {loadingContent ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" size="sm" />
                                            <div className="text-muted mt-2">Loading current content...</div>
                                        </div>
                                    ) : (
                                        <>
                                            <Form.Control
                                                as="textarea"
                                                rows={10}
                                                placeholder="Current content will be shown here. You can edit it or replace with new content..."
                                                value={text}
                                                onChange={(e) => setText(e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                Edit the content above and click "Update & Ingest" to create a new version.
                                            </Form.Text>
                                        </>
                                    )}
                                </Form.Group>
                            ) : (
                                <Form.Group>
                                    <Form.Label>Upload New File</Form.Label>
                                    {uploadPath && (
                                        <div className="alert alert-secondary mb-2">
                                            <strong>Current File:</strong> {uploadPath.split('/').pop()}
                                        </div>
                                    )}
                                    <div className="border rounded p-4 text-center bg-light">
                                        <input
                                            type="file"
                                            id="file-upload-edit"
                                            className="d-none"
                                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                                            accept=".pdf,.txt,.md,.json"
                                        />
                                        <label htmlFor="file-upload-edit" className="btn btn-outline-primary mb-2">
                                            Choose File
                                        </label>
                                        <div>
                                            {file ? (
                                                <span className="fw-bold text-success">{file.name}</span>
                                            ) : (
                                                <span className="text-muted">Supported: PDF, TXT, MD, JSON</span>
                                            )}
                                        </div>
                                    </div>
                                    <Form.Text className="text-muted">
                                        Upload a new file to create a new version and replace the active content.
                                    </Form.Text>
                                </Form.Group>
                            )}
                        </div>
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={onHide} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    variant="premium-ai"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="px-4"
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Processing...
                        </>
                    ) : (
                        <>
                            <Icon icon={FaSave} className="me-2" />
                            Update & Ingest
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditSourceModal;
